import { XCircleIcon } from '@heroicons/react/24/solid'
import { Button, Tooltip } from '@heroui/react'
import { useBulkPermanentDelete, useBulkRestore } from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useIsOverQuota } from '@qse/stores'
import { truncateTextMiddle } from '@qse/utils'
import React, { useEffect, useMemo, useState } from 'react'

import {
  CloseIcon,
  CopyIconDrop,
  DeleteIcon,
  DownloadIconDrop,
  MoveIcon,
  PreviewIcon,
  RenameIcon,
  StarIconMenu,
} from '../../icons'
import { handleFilePreview } from '../../utils/file-preview-utils'

interface SelectionToolbarProps {
  selectedCount: number
  selectedFiles: Set<string | number>
  allFiles?: any[]
  currentFolderPath?: string
  currentFolderId?: string
  parentId?: string
  onReset: () => void
  onDownload?: (fileIds: string[]) => Promise<void>
  onRename?: (fileIds: string[]) => Promise<void>
  onCopy?: (fileIds: string[]) => Promise<void>
  onDelete?: (fileIds: string[], onSuccess?: () => void) => Promise<void>
  onRestore?: (fileIds: string[]) => Promise<void>
  trashMode?: boolean
  hasUnavailableFiles?: boolean
  onDeleteGhostFiles?: (fileIds: string[]) => Promise<void>
  onToggleFavorite?: (fileIds: string[]) => Promise<void>
}

export const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  selectedCount,
  selectedFiles,
  allFiles = [],
  currentFolderPath,
  currentFolderId,
  parentId,
  onReset,
  onDownload,
  onRename,
  onCopy,
  onDelete,
  onRestore,
  trashMode = false,
  onDeleteGhostFiles,
  onToggleFavorite,
}) => {
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false)
  const isOverQuota = useIsOverQuota()
  // FIX: Move hooks to top level
  const { mutateAsync: deleteForever } = useBulkPermanentDelete()
  const { mutateAsync: restoreFiles } = useBulkRestore()

  // Handle ESC key to clear selection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle ESC if no modal is open and there are selected files
      if (event.key === 'Escape' && selectedCount > 0) {
        const activeModelStore = useActiveModelStore.getState()

        // Don't clear selection if a modal is open
        if (!activeModelStore.toggleModel) {
          onReset()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedCount, onReset])

  // Check if any selected items are folders - optimized for large selections
  const filesMap = useMemo(() => new Map(allFiles.map(f => [f.id, f])), [allFiles])
  const selectedFileIds = useMemo(() => Array.from(selectedFiles), [selectedFiles])
  const selectedFileIdStrings = useMemo(
    () => selectedFileIds.map(id => id.toString()),
    [selectedFileIds],
  )
  const hasFolders = selectedFileIds.some(fileId => {
    const file = filesMap.get(fileId)

    return file && (file.isDir || file.type === 'folder')
  })

  const isPreviewable = useMemo(() => {
    const firstFile = filesMap.get(selectedFileIds[0])

    // Preview is now available for all file types (supported and unsupported)
    return firstFile && selectedCount === 1 && !firstFile.isDir && firstFile.type !== 'folder'
  }, [filesMap, selectedCount, selectedFileIds])

  // Check if any selected files are ghost files (UPLOADING, ENCRYPTING, or FAILED)
  // Exclude directories from being treated as ghost files
  const hasGhostFiles = selectedFileIds.some(fileId => {
    const file = filesMap.get(fileId)

    return (
      file &&
      !(file.isDir || file.type === 'folder') &&
      (file.status === 'UPLOADING' || file.status === 'ENCRYPTING' || file.status === 'FAILED')
    )
  })

  // Check if any selected files are system folders
  const hasSystemFolders = selectedFileIds.some(fileId => {
    const file = filesMap.get(fileId)

    return file && file.systemFolderType
  })

  // Check if any selected files are regular files (AVAILABLE)
  const hasRegularFiles = selectedFileIds.some(fileId => {
    const file = filesMap.get(fileId)

    return file && file.status === 'AVAILABLE'
  })

  // Determine if we have a mixed selection
  const hasMixedSelection = hasGhostFiles && hasRegularFiles

  // Memoized values for better performance
  const shouldShowGhostActions = useMemo(() => {
    return !hasMixedSelection && hasGhostFiles && !trashMode
  }, [hasMixedSelection, hasGhostFiles, trashMode])

  const selectedCountText = useMemo(() => {
    return `${selectedCount.toLocaleString()} Selected`
  }, [selectedCount])

  const isSelectedFileFavorite = useMemo(() => {
    if (selectedFileIds.length === 0 || selectedFileIds.length > 1) return false

    const file = filesMap.get(selectedFileIds[0])

    return file?.isFavorite === true
  }, [filesMap, selectedFileIds])

  const handleMove = () => {
    if (selectedFileIds.length === 0 || allFiles.length === 0) return

    // Convert selected files to FileListingItemDto format - optimized for large selections
    const selectedItems = selectedFileIds
      .map(fileId => {
        const file = filesMap.get(fileId)

        if (!file) return null

        return {
          id: fileId.toString(),
          path: (file as any).path || `/${file.name}`,
          name: file.name.length > 30 ? truncateTextMiddle(file.name, 30) : file.name,
          isDir: file.type === 'folder',
          status: 'ACTIVE',
          sizeBytes: file.size,
          isFavorite: false,
          parentId: (file as any).parentId, // Include parentId from file data
          mimeType: file.type === 'folder' ? undefined : file.type,
          mediaType: file.type === 'folder' ? 'FOLDER' : 'OTHER',
          createdAt: file.lastModified,
          updatedAt: file.lastModified,
        }
      })
      .filter(Boolean)

    if (selectedItems.length === 0) return

    // Set the data for the modal
    useActiveModelStore.setState({
      data: {
        selectedItems,
        currentFolderPath,
        currentFolderId,
        parentId,
      },
      width: 'max-w-[650px]',
      scrollBehavior: 'outside',
      modelActive: ModelControlEnum.MOVE_ITEMS,
      toggleModel: true,
    })
  }

  const handleDelete = async () => {
    if (!onDelete) return
    await onDelete(selectedFileIdStrings, () => {
      onReset()
    })
  }

  const handleDownload = async () => {
    if (!onDownload) return
    try {
      await onDownload(selectedFileIdStrings)
      onReset()
    } catch (error) {
      console.error('Failed to download files:', error)
    }
  }

  const handlePreview = async () => {
    if (selectedFileIds.length !== 1) return

    const fileId = selectedFileIds[0]
    const file = filesMap.get(fileId)

    await handleFilePreview(
      fileId,
      file.name,
      file.mediaType || 'OTHER',
      file.isDir || file.type === 'folder',
      file.isFavorite || false,
      file.type,
    )
  }

  const handleRename = async () => {
    if (!onRename) return

    onRename(selectedFileIdStrings)
  }

  const handlePermanentlyDelete = () => {
    const fileIds = selectedFileIdStrings

    useActiveModelStore.setState({
      width: 'max-w-[490px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      data: {
        title:
          selectedCount === 1
            ? `Delete "${
                Array.from(selectedFiles)
                  .map(id => filesMap.get(id)?.name)
                  .filter(Boolean)[0] || 'this file'
              }" Permanently?`
            : `Delete ${selectedCount} files Permanently?`,
        message:
          selectedCount === 1
            ? 'File processing may be in progress or has failed. Large files can take considerable time to process. Canceling now will permanently remove this file.'
            : 'File processing may be in progress or has failed. Large files can take considerable time to process. Canceling now will permanently remove these files.',
        icon: '/assets/svg/warning.svg',
        actions: [
          {
            label: 'Cancel',
            color: 'default',
            variant: 'flat',
          },
          {
            label: 'Delete Permanently',
            color: 'danger',
            variant: 'solid',
            onPress: async () => {
              if (onDeleteGhostFiles) {
                await onDeleteGhostFiles(fileIds)
                onReset()
              } else {
                await deleteForever({ fileIds })
              }
            },
          },
        ],
      },
      toggleModel: true,
    })
  }

  const handleCopy = async () => {
    if (!onCopy) return
    try {
      await onCopy(selectedFileIdStrings)
      onReset()
    } catch (error) {
      console.error('Failed to copy files:', error)
    }
  }

  const handleToggleFavorite = async () => {
    if (!onToggleFavorite || selectedFileIdStrings.length === 0) return
    try {
      setIsFavoriteLoading(true)
      await onToggleFavorite(selectedFileIdStrings)
    } catch (error) {
      console.error('Failed to toggle favorites:', error)
    } finally {
      setIsFavoriteLoading(false)
    }
  }

  return (
    <div
      className="min-h-[40px] relative bg-gray-100 dark:bg-[#F1F4F933] text-gray-900 dark:!text-white w-full flex items-center justify-start gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded-lg flex-wrap selection-toolbar"
      data-selection-toolbar="true"
    >
      {/* add Close button here to deselect all */}
      <div className="flex items-center gap-1 flex-shrink-0 min-w-0">
        <Button
          isIconOnly
          aria-label="Deselect all"
          className="bg-transparent hover:bg-gray-200 dark:hover:bg-white/10 transition-colors flex-shrink-0"
          radius="full"
          size="sm"
          onPress={onReset}
        >
          <CloseIcon className="w-5 h-5 text-gray-900 dark:text-white" />
        </Button>
        <span className="text-gray-900 dark:text-white text-sm sm:text-base font-medium truncate">
          {selectedCountText}
        </span>
      </div>

      {/* Always show actions - our logic handles what to show based on file types */}
      <>
        <div className="h-4 sm:h-6 w-[1px] bg-gray-300 dark:bg-white flex-shrink-0" />
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          {/* Show mixed selection message */}
          {hasMixedSelection && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>Mix of completed and in-progress files - select the same type for actions</span>
            </div>
          )}

          {/* Show regular actions only when no mixed selection, no ghost files, and no system folders (unless in trash mode) */}
          {!hasMixedSelection &&
            !hasGhostFiles &&
            !trashMode &&
            (!hasSystemFolders || trashMode) && (
              <>
                {/* Only show download button if no folders are selected */}
                {!hasFolders && isPreviewable && (
                  <Tooltip content="Preview">
                    <Button
                      isIconOnly
                      className="bg-transparent hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                      radius="full"
                      size="sm"
                      onPress={handlePreview}
                    >
                      <PreviewIcon className="w-5 h-5 text-gray-900 dark:text-white" />
                    </Button>
                  </Tooltip>
                )}
                {!hasFolders && selectedCount <= 20 && (
                  <Tooltip content="Download">
                    <Button
                      isIconOnly
                      className="bg-transparent hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                      radius="full"
                      size="sm"
                      onPress={handleDownload}
                    >
                      <DownloadIconDrop className="w-5 h-5 text-gray-900 dark:text-white" />
                    </Button>
                  </Tooltip>
                )}
                {selectedCount === 1 && (
                  <Tooltip content="Rename (F2)">
                    <Button
                      isIconOnly
                      className="bg-transparent hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                      radius="full"
                      size="sm"
                      onPress={handleRename}
                    >
                      <RenameIcon className="w-5 h-5 text-gray-900 dark:text-white" />
                    </Button>
                  </Tooltip>
                )}
                {/* Only show copy button if no folders are selected */}
                {!hasFolders && (
                  <Tooltip
                    content={
                      isOverQuota
                        ? 'Storage quota exceeded. Delete files to free up space.'
                        : 'Copy  (Ctrl+C Ctrl+V)'
                    }
                  >
                    <span aria-disabled={isOverQuota} className="inline-flex">
                      <Button
                        isIconOnly
                        radius="full"
                        size="sm"
                        className={`bg-transparent transition-colors ${
                          isOverQuota
                            ? 'cursor-not-allowed opacity-50'
                            : 'hover:bg-gray-200 dark:hover:bg-white/10'
                        }`}
                        onPress={() => {
                          if (isOverQuota) return
                          handleCopy()
                        }}
                      >
                        <CopyIconDrop
                          className={`w-5 h-5 ${
                            isOverQuota ? 'text-gray-400' : 'text-gray-900 dark:text-white'
                          }`}
                        />
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip content="Move">
                  <Button
                    isIconOnly
                    className="bg-transparent hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                    radius="full"
                    size="sm"
                    onPress={handleMove}
                  >
                    <MoveIcon className="w-5 h-5 text-gray-900 dark:text-white" />
                  </Button>
                </Tooltip>
                {onToggleFavorite && selectedCount === 1 && (
                  <Tooltip
                    content={isSelectedFileFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Button
                      isIconOnly
                      aria-pressed={isSelectedFileFavorite}
                      className="bg-transparent transition-colors hover:bg-gray-200 dark:hover:bg-white/10"
                      isLoading={isFavoriteLoading}
                      radius="full"
                      size="sm"
                      onPress={handleToggleFavorite}
                    >
                      <StarIconMenu
                        className={`w-5 h-5 ${
                          isSelectedFileFavorite
                            ? 'text-yellow-400 dark:text-yellow-400 fill-current'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      />
                    </Button>
                  </Tooltip>
                )}

                <Tooltip content="Move to trash (Delete)">
                  <Button
                    isIconOnly
                    className="bg-transparent hover:bg-red-500 hover:text-white transition-colors"
                    radius="full"
                    size="sm"
                    onPress={handleDelete}
                  >
                    <DeleteIcon className="w-5 h-5 text-gray-900 dark:text-white" />
                  </Button>
                </Tooltip>
              </>
            )}

          {/* Show ghost file actions when only ghost files are selected */}
          {shouldShowGhostActions && (
            <>
              <Tooltip content="Delete permanently">
                <Button
                  isIconOnly
                  className="bg-transparent hover:bg-red-500 transition-all duration-150 group"
                  radius="full"
                  size="sm"
                  onPress={handlePermanentlyDelete}
                >
                  <XCircleIcon className="w-5 h-5 text-red-500 group-hover:text-white transition-all duration-150" />
                </Button>
              </Tooltip>
            </>
          )}
          {trashMode && (
            <>
              <Tooltip content="Delete Forever">
                <Button
                  isIconOnly
                  className="bg-transparent hover:bg-red-500 transition-all duration-150 group"
                  radius="full"
                  size="sm"
                  onPress={() => {
                    const fileIds = selectedFileIdStrings

                    useActiveModelStore.setState({
                      width: 'max-w-[490px]',
                      modelActive: ModelControlEnum.CONFIRMATION,
                      data: {
                        title: (() => {
                          if (selectedCount === 1) {
                            // Get the filename for single file deletion
                            const fileName =
                              Array.from(selectedFiles)
                                .map(id => filesMap.get(id)?.name)
                                .filter(Boolean)[0] || 'this item'

                            return `Delete "${fileName}" Forever?`
                          } else {
                            return `Delete ${selectedCount} items Forever?`
                          }
                        })(),
                        message:
                          selectedCount === 1
                            ? 'This file or folder will be permanently deleted and cannot be restored.'
                            : 'These files and folders will be permanently deleted and cannot be restored.',
                        icon: '/assets/svg/warning.svg',
                        actions: [
                          {
                            label: 'Cancel',
                            color: 'default',
                          },
                          {
                            label: 'Delete Forever',
                            color: 'danger',
                            variant: 'solid',
                            onPress: async () => {
                              await deleteForever({ fileIds })
                            },
                          },
                        ],
                      },
                      toggleModel: true,
                    })
                  }}
                >
                  <DeleteIcon className="w-5 h-5 text-red-500 group-hover:text-white transition-all duration-150" />
                </Button>
              </Tooltip>
              {onRestore && (
                <Tooltip content="Restore">
                  <Button
                    isIconOnly
                    className="bg-transparent hover:bg-yellow-400 transition-all duration-150 group"
                    radius="full"
                    size="sm"
                    onPress={() => {
                      const fileIds = selectedFileIdStrings

                      useActiveModelStore.setState({
                        modelActive: ModelControlEnum.CONFIRMATION,
                        width: 'max-w-[500px]',
                        data: {
                          title: (() => {
                            if (selectedCount === 1) {
                              // Get the filename for single file restoration
                              const fileName =
                                Array.from(selectedFiles)
                                  .map(id => filesMap.get(id)?.name)
                                  .filter(Boolean)[0] || 'this item'

                              return `Restore "${fileName}"?`
                            } else {
                              return `Restore ${selectedCount} items?`
                            }
                          })(),
                          message:
                            selectedCount === 1
                              ? 'This file or folder will be restored from trash.'
                              : 'These files and folders will be restored from trash.',
                          icon: '/assets/svg/warning.svg',
                          actions: [
                            {
                              label: 'Cancel',
                              variant: 'flat',
                            },
                            {
                              label: 'Restore',
                              color: 'primary',
                              variant: 'solid',
                              onPress: async () => {
                                await restoreFiles({ fileIds })
                              },
                            },
                          ],
                        },
                        toggleModel: true,
                      })
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-900 dark:text-white group-hover:text-white transition-all duration-150"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                      />
                    </svg>
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </>
    </div>
  )
}
